import { useParams } from "react-router-dom"
import { useMemo } from "react"
import { Box } from "@chakra-ui/react"
import { Provider as ChakraProvider } from "@hydrologiq/shared/chakra/Provider"
import createStylingSystem from "@hydrologiq/shared/chakra/styling/styling"
import ReportWithPeriodControls from "@hydrologiq/shared/reporting/ReportWithPeriodControls"
import ShareGraphQLProvider from "@hydrologiq/shared/providers/ShareGraphQLProvider"
import { graphqlUri } from "../config"

/**
 * The login-free public report view, served at `/share/:shareToken` outside the
 * auth gate (see `Routing.tsx`). It renders the operator's report verbatim minus
 * all chrome — no TopBar, Sidebar, page header/breadcrumb/sub-nav, and no share
 * action — for an end customer who holds the opaque share link.
 *
 * The view mounts a share-scoped Apollo transport ({@link ShareGraphQLProvider})
 * pointed at the hub's primary (experience) GraphQL endpoint and authenticated
 * with the route's `shareToken` via an `X-Share-Token` header. Because that
 * client is surfaced as the `"primary"` client, the shared report hook
 * (`useReport`) resolves against it: with the token supplied it fetches the
 * token-scoped `getPublicReport` query, which the backend resolves to its
 * granted scope/entity/tenant and clamps to the entity's available window
 * server-side (Requirements 6.2, 8.1). No client-supplied scope/entityId can
 * influence which entity is served, so this view passes none that matter — the
 * placeholder `scope`/`entityId` only select the report's render shape and are
 * ignored by the resolver.
 *
 * `ReportWithPeriodControls` provides the date-range control (Requirement 6.1)
 * and PDF download (Requirement 5.5); the share action is hidden because the
 * `shareToken` prop is set (Requirement 5.3). An invalid, revoked, or expired
 * token makes `getPublicReport` error, which the shared `Report` renders as a
 * neutral "Link unavailable" state disclosing nothing about the entity
 * (Requirements 5.2, 3.5).
 *
 * ## Why this view re-provides its own Chakra styling system (and a `#section-body` host)
 *
 * The report's cards, selectors, chart grid/cursor, and the capture `Skeleton`
 * resolve their colours through the shared theme's `planner`-prefixed CSS
 * variables (`var(--planner-colors-*)`) and semantic tokens. Chakra only emits
 * those variables where the system's `cssVarsRoot` selector matches.
 *
 * The shared theme pins that selector to
 * `:where(:host > html#section-body, :root:has(div#section-body))` — it is
 * authored for sections, which mount inside a `div#section-body` (see
 * `SectionLoader`). The hub's own root provider overrides the selector to
 * `#root` (`createStylingSystem("#root")`) for the authed chrome, but the public
 * report is section-authored content, not hub chrome.
 *
 * This view is the only place the report renders outside a section, so it has to
 * recreate the section environment the theme expects. It does two things:
 *
 *   1. Re-provides a styling system built with the shared theme's default
 *      `cssVarsRoot` (`createStylingSystem()` with no root), overriding the
 *      hub's `#root`-scoped system for this subtree only.
 *   2. Renders the report inside a `<div id="section-body">`, so the theme's
 *      `:root:has(div#section-body)` selector actually matches and the variables
 *      (and their light/dark values) are emitted. Without this host the earlier
 *      no-root system left the selector unmatched, so the variables resolved to
 *      nothing: the grid lines were invisible and the capture skeleton had no
 *      fill. The `.light` class on the host also pins colour mode to match the
 *      sections, which render light-only.
 *
 * Requirements: 5.1-5.6, 6.1, 6.3, 6.5, 8.1.
 */
export const PublicReportView = () => {
  const { shareToken = "" } = useParams<{ shareToken: string }>()

  // Shared theme's default cssVarsRoot (not the hub's "#root"), so the planner
  // CSS variables are emitted against the `:root:has(div#section-body)` selector
  // the theme targets. The `#section-body` wrapper below is what makes that
  // selector match. See the component docstring.
  const system = useMemo(() => createStylingSystem(), [])

  return (
    <ShareGraphQLProvider endpoint={graphqlUri} token={shareToken}>
      <ChakraProvider system={system} defaultTheme="light">
        {/* Scroll container. The hub's `body` is `overflow: hidden !important`
            (index.scss) and the authed app relies on its chrome owning an inner
            scroll pane; the public view has no chrome, so a tall report rendered
            straight into the body would clip with no y-scroll. This box owns the
            scroll instead: a fixed `100dvh` viewport box that scrolls internally.

            Note this box must NOT carry `id="section-body"` — the `#section-body`
            rule in index.css is an ID selector (`height: 100%`) that outranks
            Chakra's class-based style props, so it would override this height and
            collapse the scroll region. The `#section-body` host is nested inside
            instead (see below). */}
        <Box width="100%" height="100dvh" overflowY="auto" bg="background">
          {/* `id="section-body"` recreates the host the shared theme's cssVarsRoot
              (`:root:has(div#section-body)`) looks for, so the planner CSS
              variables resolve here exactly as they do inside a section. The theme
              emits the variables at `:root`, so this host only needs to exist in
              the tree — its own size is irrelevant, which is why it's safe to let
              the index.css `height: 100%` land here rather than on the scroller.
              `className="light"` pins colour mode to match the light-only sections. */}
          <Box
            id="section-body"
            className="light"
            width="100%"
            // The index.css `#section-body { height: 100% }` ID rule outranks
            // Chakra's class-based props; `!important` here lets the host grow
            // with the report instead of clamping to the scroller's 100dvh (which
            // would push the content past the host's box). Height is irrelevant to
            // the cssVarsRoot match, so forcing `auto` is safe.
            css={{ height: "auto !important" }}
          >
            {/* scope/entityId are placeholders only — the backend derives the real
                (scope, entity, tenant) from the token and ignores client input. */}
            <ReportWithPeriodControls scope="asset" entityId="" shareToken={shareToken} />
          </Box>
        </Box>
      </ChakraProvider>
    </ShareGraphQLProvider>
  )
}

export default PublicReportView
